import React, {useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";


interface VendorPartOption {
    vendorId?: number,
    vendorCode: string,
}

interface VendorPartAutocompleteProps {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    value?: VendorPartOption | null;
    onChange?: (event: React.SyntheticEvent, value: VendorPartOption | null) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: VendorPartOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: VendorPartOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

interface VendorPartSearchRequest {
    partNumber?: string,
    partId?: number;
}

interface VendorPartSearchResponse {
    data?: VendorPartOption[];
    resultFlag?: boolean;
}

const VendorPartAutocomplete: React.FC<VendorPartAutocompleteProps> = ({
                                                                           textFieldProps = {},
                                                                           form = false,
                                                                           name = undefined,
                                                                           required = false,
                                                                           partId = undefined,
                                                                           partNumber = undefined,
                                                                           setLoadingOptions: setLoadingOptionsProps,
                                                                           renderOption: customRenderOption,
                                                                           ...props
                                                                       }) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches vendorPart options based on search keywords
     * Implements pagination and keyword filtering
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @returns Array of matching vendorPart options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoadingOptions(true);
        const forApi: VendorPartSearchRequest = {partId: partId, partNumber: partNumber};
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.getVendorsByPartNumber(forApi, cancelToken) as VendorPartSearchResponse;
            if (!isMounted())
                return [];
            return response?.data ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted, partId, partNumber]);

    /**
     * Determines if two vendorPart options are equal based on their codes
     */
    const isOptionEqualToValue = useCallback((option: VendorPartOption, value: VendorPartOption | null) => option?.vendorCode === value?.vendorCode, []);

    /**
     * Gets the display label for a vendorPart option (vendorPart name)
     */
    const getOptionLabel = useCallback((option: VendorPartOption) => option?.vendorCode ?? "", []);

    /**
     * Gets the unique key for a vendorPart option (vendorPart code)
     */
    const getOptionKey = useCallback((option: VendorPartOption) => option?.vendorCode ?? "", []);

    return (
        <AutocompleteBase<VendorPartOption>
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={[]} // Initial empty array will be populated by getOptions
            {...props}
        />
    );
};

export default VendorPartAutocomplete;